import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
// RegisterPage entfernt — Registrierung deaktiviert
import { DashboardPage } from './pages/DashboardPage';
import { ReceiptListPage } from './pages/ReceiptListPage';
import { ReceiptDetailPage } from './pages/ReceiptDetailPage';
import { UploadPage } from './pages/UploadPage';
import { StatsPage } from './pages/StatsPage';
import { SettingsPage } from './pages/SettingsPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { PriceComparisonPage } from './pages/PriceComparisonPage';
import { QuickEntryPage } from './pages/QuickEntryPage';
import { ShoppingListPage } from './pages/ShoppingListPage';
import { RecurringExpensesPage } from './pages/RecurringExpensesPage';
import { TodoPage } from './pages/TodoPage';

// =============================================
// Error Boundary
// =============================================
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl font-bold">!</span>
            </div>
            <h1 className="text-xl font-bold text-neutral-800 mb-2">
              Etwas ist schiefgelaufen
            </h1>
            <p className="text-neutral-500 text-sm mb-4">
              {this.state.error?.message || 'Ein unerwarteter Fehler ist aufgetreten.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = '/';
              }}
              className="px-6 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              Zur Startseite
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// =============================================
// Protected Route Wrapper
// =============================================
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Small delay to let Zustand hydrate from sessionStorage
    const timer = setTimeout(() => setReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// =============================================
// Public Route Wrapper (redirect if already logged in)
// =============================================
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  if (!ready) return null;

  if (isAuthenticated) {
    return <Navigate to={redirectTo || '/'} replace />;
  }

  return <>{children}</>;
};

// =============================================
// App
// =============================================
export const App: React.FC = () => {
  return (
    <ErrorBoundary>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Registrierung deaktiviert — nur Login erlaubt */}

      {/* Invite Accept — öffentlich zugänglich (auch ohne Login nutzbar) */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      {/* Protected Routes with Layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/receipts" element={<ReceiptListPage />} />
        <Route path="/receipts/new" element={<UploadPage />} />
        <Route path="/shopping-list" element={<ShoppingListPage />} />
        <Route path="/todos" element={<TodoPage />} />
        <Route path="/quick-entry" element={<QuickEntryPage />} />
        <Route path="/receipts/:id" element={<ReceiptDetailPage />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/recurring" element={<RecurringExpensesPage />} />
        <Route path="/prices" element={<PriceComparisonPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
};
